import { Pause, Play, RotateCw } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { computePeaks, cssVar, formatTime, getAudioContextCtor } from './waveform-utils';

// Floor so a drag can never collapse the clip to nothing or invert start/end.
const MIN_CLIP_SECONDS = 0.2;
// Fades longer than this aren't a realistic use case for short spoken/field clips, and a fixed cap
// (rather than "whatever's left after the other fade") keeps the two sliders feeling independent
// instead of each one's range silently shrinking as the other is dragged.
const MAX_FADE_SECONDS = 5;
const WAVEFORM_HEIGHT = 88;
// Larger than the visible handle glyph — actual touch target stays comfortably tappable even
// though the drawn grip line/circle is thin, matching the ~44px touch-target guideline.
const HANDLE_HIT_WIDTH = 44;

type HandleId = 'trimStart' | 'trimEnd' | 'seek';

// Computes the gain envelope at an arbitrary point in time, so playback started mid-fade (e.g.
// after a drag-to-seek) still ramps correctly from wherever it resumes, rather than only working
// when started from trimStart.
function gainAtTime(t: number, trimStart: number, trimEnd: number, fadeIn: number, fadeOut: number): number {
  const fadeInEnd = trimStart + fadeIn;
  const fadeOutStart = trimEnd - fadeOut;
  if (fadeIn > 0 && t < fadeInEnd) return Math.max(0, Math.min(1, (t - trimStart) / fadeIn));
  if (fadeOut > 0 && t > fadeOutStart) return Math.max(0, Math.min(1, (trimEnd - t) / fadeOut));
  return 1;
}

function scheduleGain(gainParam: AudioParam, now: number, startAt: number, trimStart: number, trimEnd: number, fadeIn: number, fadeOut: number) {
  gainParam.cancelScheduledValues(now);
  const fadeInEnd = trimStart + fadeIn;
  const fadeOutStart = trimEnd - fadeOut;
  gainParam.setValueAtTime(gainAtTime(startAt, trimStart, trimEnd, fadeIn, fadeOut), now);
  if (fadeIn > 0 && startAt < fadeInEnd) {
    gainParam.linearRampToValueAtTime(1, now + (fadeInEnd - startAt));
  }
  if (fadeOut > 0) {
    const holdUntil = Math.max(startAt, fadeOutStart);
    if (holdUntil > startAt) gainParam.setValueAtTime(1, now + (holdUntil - startAt));
    gainParam.linearRampToValueAtTime(0, now + (trimEnd - startAt));
  }
}

export interface AudioTrimmerProps {
  file: File;
  duration: number;
  trimStart: number;
  trimEnd: number;
  fadeIn: number;
  fadeOut: number;
  onTrimChange: (trimStart: number, trimEnd: number) => void;
  onFadeChange: (fadeIn: number, fadeOut: number) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

// Web Audio-based waveform editor: decode once via decodeAudioData, draw peaks on a canvas, drag
// vertical handles to trim and corner handles to fade — no external library. Playback is driven by
// an AudioBufferSourceNode + GainNode (not a plain <audio> element) specifically so the preview can
// respect the CURRENT trim window and fade ramps live, matching what ffmpeg will actually render.
export function AudioTrimmer({ file, duration, trimStart, trimEnd, fadeIn, fadeOut, onTrimChange, onFadeChange, t }: AudioTrimmerProps) {
  const fadeInId = useId();
  const fadeOutId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioBufferRef = useRef<AudioBuffer | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const draggingRef = useRef<HandleId | null>(null);

  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const [peaks, setPeaks] = useState<{ min: Float32Array; max: Float32Array } | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playhead, setPlayhead] = useState(trimStart);

  // Decode once per file — a fresh AudioContext per mount keeps this component fully self-contained.
  // Unlike WaveformPlayer (which fetches a signed URL that can expire), this decodes a local File
  // object, so a failure here almost always means a genuinely corrupt/unsupported recording rather
  // than a network/auth issue — "try again" just re-attempts the same decode.
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setLoadError(false);
    const ctx = new (getAudioContextCtor())();
    audioContextRef.current = ctx;
    file.arrayBuffer()
      .then((buf) => ctx.decodeAudioData(buf))
      .then((buffer) => {
        if (cancelled) return;
        audioBufferRef.current = buffer;
        setReady(true);
      })
      .catch(() => { if (!cancelled) setLoadError(true); });
    return () => {
      cancelled = true;
      stopPlayback();
      void ctx.close();
    };
  }, [file, retryTick]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Peak resolution follows the actual rendered width (~2px/bucket) rather than a fixed count, so
  // the waveform stays crisp instead of looking blocky on a wide drawer or muddy on a narrow one.
  useEffect(() => {
    const buffer = audioBufferRef.current;
    if (!ready || !buffer || !containerWidth) return;
    const buckets = Math.min(1000, Math.max(100, Math.floor(containerWidth / 2)));
    setPeaks(computePeaks(buffer, buckets));
  }, [ready, containerWidth]);

  useEffect(() => {
    setPlayhead((p) => Math.min(Math.max(p, trimStart), trimEnd));
  }, [trimStart, trimEnd]);

  useEffect(() => {
    draw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peaks, containerWidth, trimStart, trimEnd, fadeIn, fadeOut, playhead]);

  function timeToX(time: number): number {
    if (duration <= 0) return 0;
    return (time / duration) * containerWidth;
  }

  function xToTime(clientX: number): number {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return 0;
    const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return frac * duration;
  }

  function draw() {
    const canvas = canvasRef.current;
    if (!canvas || !peaks || !containerWidth) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = containerWidth * dpr;
    canvas.height = WAVEFORM_HEIGHT * dpr;
    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${WAVEFORM_HEIGHT}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, containerWidth, WAVEFORM_HEIGHT);

    const muted = cssVar(canvas, '--color-ws-muted', '#94a3b8');
    const primary = cssVar(canvas, '--color-ws-primary', '#2563eb');
    const mid = WAVEFORM_HEIGHT / 2;

    // Waveform bars — each bar's height is scaled by the actual gain envelope at that point in
    // time, so the fade regions visibly taper the waveform itself down to a sliver instead of
    // needing a separate overlay shape to interpret (this directly mirrors what fade will sound
    // like, since it's the same gainAtTime() the real playback preview schedules from).
    ctx.fillStyle = muted;
    const barWidth = containerWidth / peaks.min.length;
    for (let i = 0; i < peaks.min.length; i++) {
      const bucketTime = ((i + 0.5) / peaks.min.length) * duration;
      const gain = gainAtTime(bucketTime, trimStart, trimEnd, fadeIn, fadeOut);
      const x = i * barWidth;
      const yTop = mid - peaks.max[i]! * gain * mid;
      const yBottom = mid - peaks.min[i]! * gain * mid;
      ctx.fillRect(x, yTop, Math.max(1, barWidth - 1), Math.max(1, yBottom - yTop));
    }

    // Dim the trimmed-out head/tail
    const startX = timeToX(trimStart);
    const endX = timeToX(trimEnd);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
    if (startX > 0) ctx.fillRect(0, 0, startX, WAVEFORM_HEIGHT);
    if (endX < containerWidth) ctx.fillRect(endX, 0, containerWidth - endX, WAVEFORM_HEIGHT);

    // Playhead
    ctx.strokeStyle = primary;
    ctx.lineWidth = 2;
    const playheadX = timeToX(playhead);
    ctx.beginPath();
    ctx.moveTo(playheadX, 0);
    ctx.lineTo(playheadX, WAVEFORM_HEIGHT);
    ctx.stroke();
  }

  function stopPlayback() {
    if (sourceNodeRef.current) {
      sourceNodeRef.current.onended = null;
      try { sourceNodeRef.current.stop(); } catch { /* already stopped */ }
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }
    if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }

  function playFrom(startAt: number) {
    const ctx = audioContextRef.current;
    const buffer = audioBufferRef.current;
    if (!ctx || !buffer) return;
    stopPlayback();
    if (ctx.state === 'suspended') void ctx.resume();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gain = ctx.createGain();
    source.connect(gain).connect(ctx.destination);
    const now = ctx.currentTime;
    scheduleGain(gain.gain, now, startAt, trimStart, trimEnd, fadeIn, fadeOut);
    source.start(0, startAt, Math.max(0, trimEnd - startAt));
    sourceNodeRef.current = source;
    setPlaying(true);
    // Natural end-of-clip — snap the playhead back to the start of the selection so the next
    // Play press replays the whole thing, instead of "resuming" from a fraction of a second
    // before trimEnd (which sounded like playback was stuck).
    source.onended = () => { setPlaying(false); setPlayhead(trimStart); };
    const tick = () => {
      if (!audioContextRef.current) return;
      const elapsed = audioContextRef.current.currentTime - now;
      const t = Math.min(trimEnd, startAt + elapsed);
      setPlayhead(t);
      if (t < trimEnd) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }

  function togglePlayPause() {
    if (playing) {
      stopPlayback();
      setPlaying(false);
      return;
    }
    const startAt = Math.min(Math.max(playhead, trimStart), Math.max(trimStart, trimEnd - 0.05));
    playFrom(startAt);
  }

  function beginDrag(handle: HandleId) {
    if (playing) { stopPlayback(); setPlaying(false); }
    draggingRef.current = handle;
  }

  function applyHandleTime(handle: HandleId, rawTime: number) {
    const t = Math.min(duration, Math.max(0, rawTime));
    if (handle === 'seek') {
      setPlayhead(Math.min(Math.max(t, trimStart), trimEnd));
      return;
    }
    if (handle === 'trimStart') {
      const newStart = Math.min(t, trimEnd - MIN_CLIP_SECONDS);
      const clampedStart = Math.max(0, newStart);
      const availableForFadeIn = trimEnd - clampedStart - fadeOut;
      onTrimChange(clampedStart, trimEnd);
      if (fadeIn > availableForFadeIn) onFadeChange(Math.max(0, availableForFadeIn), fadeOut);
      return;
    }
    // trimEnd
    const newEnd = Math.max(t, trimStart + MIN_CLIP_SECONDS);
    const clampedEnd = Math.min(duration, newEnd);
    const availableForFadeOut = clampedEnd - trimStart - fadeIn;
    onTrimChange(trimStart, clampedEnd);
    if (fadeOut > availableForFadeOut) onFadeChange(fadeIn, Math.max(0, availableForFadeOut));
  }

  function handleHandlePointerDown(handle: HandleId, e: React.PointerEvent) {
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    (e.target as HTMLElement).focus();
    beginDrag(handle);
    applyHandleTime(handle, xToTime(e.clientX));
  }

  function handleContainerPointerDown(e: React.PointerEvent) {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    beginDrag('seek');
    applyHandleTime('seek', xToTime(e.clientX));
  }

  function handlePointerMove(e: React.PointerEvent) {
    const handle = draggingRef.current;
    if (!handle) return;
    applyHandleTime(handle, xToTime(e.clientX));
  }

  function handlePointerUp() {
    draggingRef.current = null;
  }

  function handleHandleKeyDown(handle: HandleId, current: number, e: React.KeyboardEvent) {
    const step = e.shiftKey ? 1 : 0.1;
    if (e.key === 'ArrowLeft') { e.preventDefault(); applyHandleTime(handle, current - step); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); applyHandleTime(handle, current + step); }
    else if (e.key === 'Home') { e.preventDefault(); applyHandleTime(handle, handle === 'seek' ? trimStart : 0); }
    else if (e.key === 'End') { e.preventDefault(); applyHandleTime(handle, handle === 'seek' ? trimEnd : duration); }
  }

  const selectedDuration = Math.max(0, trimEnd - trimStart);
  // Independent of the OTHER fade's current value on purpose — halving the selection is only ever
  // a backstop for very short clips, not something that shrinks as you drag the other slider.
  const maxFade = Math.min(MAX_FADE_SECONDS, selectedDuration / 2);

  return (
    <div className='flex flex-col gap-2 rounded-lg bg-ws-btn p-4'>
      <div className='flex items-center gap-3'>
        <button
          type='button'
          onClick={togglePlayPause}
          disabled={!ready}
          aria-label={playing ? t('blocks.sound.pause') : t('blocks.sound.play')}
          className='flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ws-primary text-ws-btn-on-primary hover:opacity-90 disabled:opacity-50'
        >
          {playing ? <Pause className='h-4 w-4' /> : <Play className='h-4 w-4 translate-x-[1px]' />}
        </button>
        <span className='text-xs tabular-nums text-ws-muted'>
          {formatTime(playhead - trimStart)} / {formatTime(selectedDuration)}
        </span>
        <span className='ml-auto text-xs text-ws-muted'>{t('blocks.sound.selectedOfTotal', { selected: formatTime(selectedDuration), total: formatTime(duration) })}</span>
      </div>

      <div
        ref={containerRef}
        role='slider'
        aria-label={t('blocks.sound.scrub')}
        aria-valuemin={trimStart}
        aria-valuemax={trimEnd}
        aria-valuenow={playhead}
        tabIndex={0}
        onKeyDown={(e) => {
          // Trim/fade handles are nested inside this same container and bubble their own keydown
          // up here too — only handle it at the container level when the container itself is the
          // actual target, or an arrow-key press on a handle would seek AND move that handle at once.
          if (e.target !== e.currentTarget) return;
          handleHandleKeyDown('seek', playhead, e);
        }}
        className='relative touch-none select-none focus-visible:[outline:2px_auto]'
        style={{ height: WAVEFORM_HEIGHT }}
        onPointerDown={handleContainerPointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <canvas ref={canvasRef} aria-hidden='true' className='absolute inset-0 h-full w-full rounded-md' />
        {!ready && !loadError && (
          <div className='absolute inset-0 flex items-center justify-center'>
            <p className='text-xs italic text-ws-muted'>{t('blocks.sound.loadingWaveform')}</p>
          </div>
        )}
        {loadError && (
          <div className='absolute inset-0 flex flex-col items-center justify-center gap-1.5 rounded-md bg-ws-btn'>
            <p role='alert' className='text-xs text-ws-warning-fg'>{t('blocks.sound.loadError')}</p>
            <button
              type='button'
              onClick={() => setRetryTick((n) => n + 1)}
              className='flex items-center gap-1 rounded-md border border-ws-border bg-ws-surface px-2 py-1 text-xs font-medium text-ws-text hover:bg-ws-hover'
            >
              <RotateCw className='h-3 w-3' />
              {t('blocks.sound.tryAgain')}
            </button>
          </div>
        )}

        {/* Trim handles — full-height vertical grips */}
        <button
          type='button'
          role='slider'
          aria-label={t('blocks.sound.trimStart')}
          aria-valuemin={0}
          aria-valuemax={trimEnd}
          aria-valuenow={trimStart}
          tabIndex={0}
          onKeyDown={(e) => handleHandleKeyDown('trimStart', trimStart, e)}
          onPointerDown={(e) => handleHandlePointerDown('trimStart', e)}
          className='absolute top-0 flex items-center justify-center focus-visible:[outline:2px_auto]'
          style={{ left: timeToX(trimStart), width: HANDLE_HIT_WIDTH, height: WAVEFORM_HEIGHT, transform: 'translateX(-50%)', cursor: 'ew-resize', touchAction: 'none' }}
        >
          <span className='h-full w-1 rounded-full bg-ws-primary' />
        </button>
        <button
          type='button'
          role='slider'
          aria-label={t('blocks.sound.trimEnd')}
          aria-valuemin={trimStart}
          aria-valuemax={duration}
          aria-valuenow={trimEnd}
          tabIndex={0}
          onKeyDown={(e) => handleHandleKeyDown('trimEnd', trimEnd, e)}
          onPointerDown={(e) => handleHandlePointerDown('trimEnd', e)}
          className='absolute top-0 flex items-center justify-center focus-visible:[outline:2px_auto]'
          style={{ left: timeToX(trimEnd), width: HANDLE_HIT_WIDTH, height: WAVEFORM_HEIGHT, transform: 'translateX(-50%)', cursor: 'ew-resize', touchAction: 'none' }}
        >
          <span className='h-full w-1 rounded-full bg-ws-primary' />
        </button>
      </div>

      {/* Fade — plain sliders rather than draggable handles on the waveform itself: the corner-drag
          gesture collided spatially with the trim handles and wasn't an obvious affordance. The
          waveform still shades the fade region above for feedback, but adjusting it happens here. */}
      <div className='grid grid-cols-2 gap-4 pt-1'>
        <div className='flex flex-col gap-1'>
          <div className='flex items-center justify-between text-xs text-ws-muted'>
            <label htmlFor={fadeInId}>{t('blocks.sound.fadeIn')}</label>
            <span className='tabular-nums'>{fadeIn.toFixed(1)}s</span>
          </div>
          <input
            id={fadeInId}
            type='range'
            min={0}
            max={maxFade}
            step={0.1}
            value={Math.min(fadeIn, maxFade)}
            onChange={(e) => onFadeChange(Math.min(Number(e.target.value), maxFade), fadeOut)}
            className='h-1.5 accent-ws-primary'
          />
        </div>
        <div className='flex flex-col gap-1'>
          <div className='flex items-center justify-between text-xs text-ws-muted'>
            <label htmlFor={fadeOutId}>{t('blocks.sound.fadeOut')}</label>
            <span className='tabular-nums'>{fadeOut.toFixed(1)}s</span>
          </div>
          <input
            id={fadeOutId}
            type='range'
            min={0}
            max={maxFade}
            step={0.1}
            value={Math.min(fadeOut, maxFade)}
            onChange={(e) => onFadeChange(fadeIn, Math.min(Number(e.target.value), maxFade))}
            className='h-1.5 accent-ws-primary'
          />
        </div>
      </div>
    </div>
  );
}
